import { TimeService } from "../../../../../services/TimeService";

export type TimeZoneMode = "server" | "utc" | "local";

export const TIME_ZONE_MODES: TimeZoneMode[] = ["server", "utc", "local"];

export const DEFAULT_TIME_ZONE_MODE: TimeZoneMode = "server";

export interface TimeParts {
	year: number;
	month: number;
	day: number;
	hours: number;
	minutes: number;
	seconds: number;
	milliseconds: number;
}

export const pad = (value: number, size = 2) =>
	value.toString().padStart(size, "0");

export const getServerTimeZone = () =>
	TimeService.getInstance().getServerTimeZone();

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getZonedFormatter = (timeZone: string) => {
	const cached = formatterCache.get(timeZone);
	if (cached) return cached;

	const formatter = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hour12: false,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
	formatterCache.set(timeZone, formatter);
	return formatter;
};

const getLocalParts = (date: Date): TimeParts => ({
	year: date.getFullYear(),
	month: date.getMonth(),
	day: date.getDate(),
	hours: date.getHours(),
	minutes: date.getMinutes(),
	seconds: date.getSeconds(),
	milliseconds: date.getMilliseconds(),
});

const getZonedParts = (date: Date, timeZone: string): TimeParts => {
	try {
		const parts = getZonedFormatter(timeZone).formatToParts(date);
		const read = (type: string) =>
			Number(parts.find((part) => part.type === type)?.value ?? 0);
		const hours = read("hour");

		return {
			year: read("year"),
			month: read("month") - 1,
			day: read("day"),
			hours: hours === 24 ? 0 : hours,
			minutes: read("minute"),
			seconds: read("second"),
			milliseconds: date.getMilliseconds(),
		};
	} catch {
		return getLocalParts(date);
	}
};

export const getTimeParts = (time: number, mode: TimeZoneMode): TimeParts => {
	const date = new Date(time);

	if (mode === "utc") {
		return {
			year: date.getUTCFullYear(),
			month: date.getUTCMonth(),
			day: date.getUTCDate(),
			hours: date.getUTCHours(),
			minutes: date.getUTCMinutes(),
			seconds: date.getUTCSeconds(),
			milliseconds: date.getUTCMilliseconds(),
		};
	}

	if (mode === "server") {
		return getZonedParts(date, getServerTimeZone());
	}

	return getLocalParts(date);
};

export const formatDateTime = (
	time: number,
	mode: TimeZoneMode,
	withMilliseconds = false,
): string => {
	const date = new Date(time);
	if (Number.isNaN(date.getTime())) return "";

	const parts = getTimeParts(time, mode);
	const day = `${pad(parts.month + 1)}/${pad(parts.day)}/${parts.year}`;
	const clock = `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
	const base = `${day} ${clock}`;

	return withMilliseconds ? `${base}.${pad(parts.milliseconds, 3)}` : base;
};

export const formatTime = (
	time: number,
	mode: TimeZoneMode,
	withMilliseconds = false,
): string => {
	const date = new Date(time);
	if (Number.isNaN(date.getTime())) return "";

	const parts = getTimeParts(time, mode);
	const clock = `${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;

	return withMilliseconds
		? `${clock}.${pad(parts.milliseconds, 3)}`
		: clock;
};

export const isSameDay = (
	first: number,
	second: number,
	mode: TimeZoneMode,
): boolean => {
	const a = getTimeParts(first, mode);
	const b = getTimeParts(second, mode);

	return a.year === b.year && a.month === b.month && a.day === b.day;
};

export const toPickerTimeZone = (mode: TimeZoneMode) => {
	if (mode === "utc") return "UTC";
	if (mode === "local") return "system";
	return getServerTimeZone();
};

export const TIME_ZONE_SHORT_LABELS: Record<TimeZoneMode, string> = {
	server: "Server",
	utc: "UTC",
	local: "Local",
};

export const getTimeZoneLabel = (mode: TimeZoneMode) => {
	if (mode !== "server") return TIME_ZONE_SHORT_LABELS[mode];

	const zone = getServerTimeZone();
	return zone ? `Server (${zone})` : "Server";
};
